import { useMutation } from '@tanstack/react-query';
import {
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  ModalProps,
  Row,
  Upload,
  UploadFile,
} from 'antd';
import { FormInstance } from 'antd/es/form';
import { DefaultOptionType } from 'antd/es/select';
import { UploadFileStatus } from 'antd/es/upload/interface';
import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { ChangeEvent, memo, useEffect, useMemo, useRef, useState } from 'react';

import { CustomerApis } from 'apis/CustomerApis';
import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import {
  GET_ALL_CITY,
  GET_ALL_DISTRICT,
  GET_ALL_WARD,
} from 'apis/GraphQL/Queries/cityQuery';
import {
  GET_ALL_CUSTOMER,
  GET_REGION,
} from 'apis/GraphQL/Queries/customerQuery';
import { Loader } from 'assets/svg';
import { Button, InputForm, SelectForm } from 'components';
import ImportIcon from 'components/Icons/ImportIcon';
import ModalMapLocation from 'components/Map/ModalMapLocation';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { SelectForm_v2 } from 'components/Select/SelectForm';
import { useUser } from 'contexts/UserProvider';
import { IParamQraphQL } from 'interfaces/GraphQL/GraphQL';
import {
  convertDateToBE,
  toastError,
  toastSuccess,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { useAmlCheck } from 'hooks/useAmlCheck';

type IProps = {
  data: any;
  isOpenModal: boolean;
  refetch: () => void;
  onCancel: () => void;
  onSubmit: (data: any) => void;
  setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
} & ModalProps;

interface ISelectedAddress {
  address?: string;
  city?: string;
  district?: string;
  ward?: string;
}

interface IPrevValue {
  cccd: string;
  taxCode: string;
}

interface ICheckDuplicateParams {
  fieldName: string;
  fieldValue: string;
  errorMessage: string[];
  apiFn: (value: string) => Promise<AxiosResponse<any, any>>;
}

const GenderOptions = [
  {
    value: 'Nam',
    label: 'Nam',
  },
  {
    value: 'Nữ',
    label: 'Nữ',
  },
  {
    value: 'Khác',
    label: 'Khác',
  },
];

const typeCustomerOptions = [
  {
    value: 'Cá nhân',
    label: 'Cá nhân',
  },
  {
    value: 'Doanh nghiệp',
    label: 'Doanh nghiệp',
  },
];

const idTypeOptionsDN = [{ value: 'cccd', label: 'CCCD' }];

const idTypeOptionsCN = [
  { value: 'cccd', label: 'CCCD' },
  { value: 'passport', label: 'Passport' },
];

const noteOptions = [
  { label: 'Vàng thường', value: 'T' },
  { label: 'Vàng ý', value: 'Y' },
  { label: 'Kim cương', value: 'M' },
  { label: 'Khác', value: 'K' },
];

const initTypeCustomer = 'Doanh nghiệp';
const initPrev: IPrevValue = {
  cccd: '',
  taxCode: '',
};

const { PlusOutlined, EnvironmentOutlined } = icons;

const ModalAddCusPage = ({
  data,
  isOpenModal,
  refetch,
  onCancel,
  onSubmit,
  setIsOpenModal,
  ...props
}: IProps) => {
  const { user } = useUser();
  const [form] = Form.useForm();
  const { checkAndProceed, amlModalContext } = useAmlCheck();

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [selectedAddress, setSelectedAddress] = useState<ISelectedAddress>();

  const [optionsCity, setOptionsCity] = useState<any>([]);
  const [optionsWard, setOptionsWard] = useState<any>([]);
  const [optionsDistrict, setOptionsDistrict] = useState<any>([]);
  const [optionsCityNew, setOptionsCityNew] = useState<any>([]);
  const [optionsWardNew, setOptionsWardNew] = useState<any>([]);
  const [regionItems, setRegionItems] = useState<any>([]);

  const [mainCustomerData, setMainCustomerData] =
    useState<ILazySelectData>(initLazySelectData);

  const [center, setCenter] = useState<any>();
  const [prevValue, setPrevValue] = useState<IPrevValue>(initPrev);
  const [typeCustomer, setTypeCustomer] = useState<any>(initTypeCustomer);
  const [selectedIdType, setSelectedIdType] = useState<'cccd' | 'passport'>(
    'cccd',
  );

  const [isOpenToaDo, setIsOpenToaDo] = useState(false);
  const [isSAPCustomer, setIsSAPCustomer] = useState(false);
  const [isMainCustomer, setIsMainCustomer] = useState(false);
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [isOpenNotifyModal, setIsOpenNotifyModal] = useState(false);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const isEdit = useMemo(() => Object.keys(data).length > 0, [data]);
  const placement = useMemo(() => {
    if (!selectedAddress) return;

    const { city, district, ward, address } = selectedAddress;

    if (address && ward && city) {
      return [address, ward, district, city]
        .filter((item) => !!item)
        .join(', ');
    }

    return '';
  }, [selectedAddress]);

  const customerMutation = useMutation({
    mutationFn: (id: string) =>
      axiosGraphQL.post('/graphql', {
        query: GET_ALL_CUSTOMER,
        variables: {
          input: {
            id: {
              equals: id,
            },
          },
        },
      }),
  });

  const { mutateAsync: checkDuplicateTaxId } = useMutation({
    mutationFn: (data: any) => CustomerApis.CheckDuplicateTaxId(data),
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutateAsync: checkDuplicateCCCD } = useMutation({
    mutationFn: (value: string) => CustomerApis.checkDuplicateCCCD(value),
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: checkUserInfo } = useMutation({
    mutationFn: (id: string) => CustomerApis.checkUserInfo(id),
    onSuccess: (res) => {
      const data = res?.data?.checkUserSap;

      if (data && data?.status === 'SAP') {
        setIsSAPCustomer(true);
        return;
      }
      setIsSAPCustomer(false);
    },
  });

  const { mutateAsync: checkMainCustomer } = useMutation({
    mutationFn: (mainCustomerId: string) =>
      CustomerApis.checkMainCustomer(mainCustomerId),
    onSuccess: (res) => {
      const isMainCustomer = res?.data?.getCustomer?.items?.length > 0;
      setIsMainCustomer(isMainCustomer);
    },
  });

  const { mutateAsync: sendCustomerToSAP, isPending: isSendCustomerPending } =
    useMutation({
      mutationFn: (id: string) => CustomerApis.sendCustomerToSAP(id),
    });

  const { mutate: getKHCha, isPending: isGetKHChaPending } = useMutation({
    mutationFn: (data: any) => CustomerApis.GetCustomerInPlantComBoBox(data),
    onSuccess: (res, variables) => {
      const items = res.data?.getCustomerInPlantComBoBox?.items;
      const totalItems =
        res.data?.getCustomerInPlantComBoBox?.pageInfo.totalItems;

      const mainCustomer = variables?.mainCustomer
        ? [
            {
              key: variables.mainCustomer?.id,
              label: `${variables.mainCustomer?.sapCustomerId} - ${variables.mainCustomer?.storeName}`,
              value: variables.mainCustomer?.id,
            },
          ]
        : [];

      const options = items?.map((item: any) => ({
        key: item.id,
        label: `${item.sapCustomerId} - ${item.storeName}`,
        value: item.id,
      }));

      const formattedOptions = [...mainCustomer, ...options].filter(
        (option, index, arr) =>
          arr.findIndex((item) => item.key === option.key) === index,
      );

      setMainCustomerData({
        options: formattedOptions,
        totalItems: totalItems,
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: createCusNew, isPending: isCreateCusPending } = useMutation({
    mutationFn: (data: any) => CustomerApis.createCus(data),
    onSuccess: (res) => {
      const plantId = user?.plantCode;
      const customerId = res.data.createCustomer.id;
      const { LineBusiness, noteLineBusiness } = form.getFieldsValue();

      if (LineBusiness?.length > 0) {
        CreateCustomerLineProduct({
          customerId,
          LineBusiness,
          noteLineBusiness,
        });
      }

      createCustomerPlant({ customerId, plantId });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: createCustomerPlant, isPending: isCreatePlantPending } =
    useMutation({
      mutationFn: (data: any) => CustomerApis.CreateCustomerPlant(data),
      onSuccess: async (res) => {
        toastSuccess('Khách hàng mới đã được tạo thành công tại DMS.');
        onSubmit(res);
        form.resetFields();
        setTypeCustomer(undefined);
        setFileList([]);
        setIsOtherSelected(false);
        setCenter(null);
      },
      onError: (error) => {
        toastError('Tạo mới thất bại');
        console.log(error);
      },
    });

  const {
    mutateAsync: CreateCustomerLineProduct,
    isPending: isCreateLinePending,
  } = useMutation({
    mutationFn: (data: any) => CustomerApis.CreateCustomerLineProduct(data),
    onError: (error) => {
      toastError('Tạo mới thất bại');
      console.log(error);
    },
  });

  const { mutateAsync: deleteCustomerLineProduct } = useMutation({
    mutationFn: (params: any) => CustomerApis.deleteCustomerLineProduct(params),
    onError: (error) => {
      toastError('Tạo mới thất bại');
      console.log(error);
    },
  });

  const { mutateAsync: updateCustomer, isPending: isUpdateCustomerPending } =
    useMutation({
      mutationFn: (params: any) => CustomerApis.updateCustomer(params),
      onSuccess: async (res, variables) => {
        if (!res.data) {
          toastError('Cập nhật thông tin thất bại');
          return;
        }

        const isInValid =
          !data?.tcustomerLineProduct ||
          !Array.isArray(data?.tcustomerLineProduct);

        if (isInValid) return;
        const { LineBusiness, noteLineBusiness } = form.getFieldsValue();
        const isChangeLineBusinessNote =
          noteLineBusiness !==
          data?.tcustomerLineProduct?.find(
            (item: any) => item?.lineProductId === 'K',
          )?.note;

        const paramsDeleteLineProduct = data?.tcustomerLineProduct
          ?.filter(
            (item: any) =>
              !LineBusiness?.some((productLine: any) =>
                item.lineProductId === 'K'
                  ? item.lineProductId !== productLine &&
                    !isChangeLineBusinessNote
                  : item.lineProductId === productLine,
              ),
          )
          .map((item: any) => item.id);

        const paramsInsertLineProduct = LineBusiness?.filter(
          (item: any) =>
            !data?.tcustomerLineProduct?.some((lineProduct: any) =>
              item === 'K'
                ? item !== lineProduct.lineProductId &&
                  !isChangeLineBusinessNote
                : item === lineProduct.lineProductId,
            ),
        );

        paramsDeleteLineProduct.length &&
          (await deleteCustomerLineProduct(paramsDeleteLineProduct));

        paramsInsertLineProduct?.length &&
          (await CreateCustomerLineProduct({
            customerId: data.id,
            LineBusiness: paramsInsertLineProduct,
            noteLineBusiness,
          }));

        const id = res.data?.updateCustomer?.id;
        if (variables?.isSendToSAP && id) {
          try {
            const response: any = await sendCustomerToSAP(id);

            if (response?.data) {
              handleCloseModal();
              toastSuccess(response?.message);
            }
          } catch (error: any) {
            toastError(
              `Lấy mã SAP thất bại: ${error?.response?.data?.message}`,
            );
          }
        } else {
          handleCloseModal();
          toastSuccess('Cập nhật khách hàng thành công.');
        }

        refetch();
      },
      onError: (error) => {
        console.log(error);
        toastError('Cập nhật khách hàng thất bại.');
      },
    });

  const mutationCity = useMutation({
    mutationKey: ['getAllCity'],
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onSuccess: (res) => {
      setOptionsCity(
        res.data?.getCity
          ? res.data?.getCity.items.map((item: any) => ({
              label: item.name,
              value: item.id,
            }))
          : [],
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const mutationDistrict = useMutation({
    mutationKey: ['getAllDistrict'],
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onSuccess: (res) => {
      setOptionsDistrict(
        res.data?.getDistrict
          ? res.data?.getDistrict.items.map((item: any) => ({
              label: item.name,
              value: item.id,
            }))
          : [],
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const mutationWard = useMutation({
    mutationKey: ['getAllWard'],
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onSuccess: (res) => {
      setOptionsWard(
        res.data?.getWard
          ? res.data?.getWard.items.map((item: any) => ({
              label: item.name,
              value: item.id,
            }))
          : [],
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const mutationRegion = useMutation({
    mutationKey: ['getRegion'],
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onSuccess: (res) => {
      setOptionsWard(
        res.data?.getRegion
          ? res.data?.getRegion.items.map((item: any) => ({
              label: item.wardName || item.tward?.name,
              value: item.wardId,
            }))
          : [],
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const mutationRegionNew = useMutation({
    mutationKey: ['getRegionNew'],
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onSuccess: (res) => {
      const items = res.data?.getRegion?.items || [];
      setRegionItems(items);

      const citiesMap: { [key: string]: string } = {};
      items.forEach((item: any) => {
        const cityName = item.cityName || item.tcity?.name;
        if (item.cityId && cityName) {
          citiesMap[item.cityId] = cityName;
        }
      });
      const cityOptions = Object.keys(citiesMap).map((id) => ({
        label: citiesMap[id],
        value: id,
      }));
      setOptionsCityNew(cityOptions);

      if (data?.cityNewId) {
        const filteredWards = items
          .filter((item: any) => item.cityId === data.cityNewId)
          .map((item: any) => ({
            label: item.wardName || item.tward?.name,
            value: item.wardId,
          }));
        setOptionsWardNew(filteredWards);

        if (data?.wardNewId) {
          const selectedRegion = items.find(
            (item: any) =>
              item.cityId === data.cityNewId && item.wardId === data.wardNewId,
          );
          if (selectedRegion?.regionName) {
            form.setFieldsValue({ regionName: selectedRegion.regionName });
          }
        }
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handleChangeCityNew = (value: any, option?: DefaultOptionType) => {
    form.setFieldsValue({
      wardNewId: undefined,
      regionName: undefined,
    });

    handleChangeAddress('city', option?.label?.toString());
    setSelectedAddress((prev) => ({
      ...prev,
      ward: undefined,
    }));

    if (!value) {
      setOptionsWardNew([]);
      return;
    }

    const filteredWards = regionItems
      .filter((item: any) => item.cityId === value)
      .map((item: any) => ({
        label: item.wardName || item.tward?.name,
        value: item.wardId,
      }));
    setOptionsWardNew(filteredWards);
  };

  const handleChangeWardNew = (value: any, option?: DefaultOptionType) => {
    handleChangeAddress('ward', option?.label?.toString());
    const currentCityId = form.getFieldValue('cityNewId');
    const selectedRegion = regionItems.find(
      (item: any) => item.cityId === currentCityId && item.wardId === value,
    );
    form.setFieldsValue({
      regionName: selectedRegion?.regionName || '',
    });
  };

  useEffect(() => {
    mutationCity.mutate({
      query: GET_ALL_CITY,
    });
    mutationRegionNew.mutate({
      query: GET_REGION,
      variables: {
        take: 5000,
      },
    });
  }, [data?.cityNewId]);

  useEffect(() => {
    if (!isOpenModal) return;

    getKHCha({
      currentCust: data.id,
      typeCust: data.typeSap,
      mainCustomer: data.mainCustomer,
    });

    setIsSAPCustomer(false);
    setIsMainCustomer(false);
    setSelectedAddress({} as ISelectedAddress);
  }, [isOpenModal]);

  useEffect(() => {
    if (!isEdit) {
      setPrevValue(initPrev);
      return;
    }

    checkUserInfo(data.id);
    checkMainCustomer(data.id);

    let titleSAP = null;
    switch (data.titleSap) {
      case '0001':
        titleSAP = 'Nữ';
        break;
      case '0002':
        titleSAP = 'Nam';
        break;
      case '0003':
        titleSAP = 'Công ty';
        break;
      default:
        titleSAP = data.typeSap === '1' ? undefined : 'Công ty';
        break;
    }

    try {
      const typeCustomer = data.typeSap === '1' ? 'Cá nhân' : 'Doanh nghiệp';

      const initialRegion = regionItems.find(
        (item: any) =>
          item.cityId === data?.cityNewId && item.wardId === data?.wardNewId,
      );

      const fieldsValue = {
        ...data,
        typeCustomer: typeCustomer,
        DanhXung: titleSAP,
        IDCusParent: data?.mainCustomer?.id,
        Surname: data.firstName,
        name: data.name,
        nameCustomer: data.storeName,
        nameShort: data.shortName,
        nameIndividualContact: data.storeName,
        taxCode: data.taxId,
        sdt: data.phone,
        Gender: data.gender,
        birthday: data.dayofbirth ? dayjs(data.dayofbirth) : '',
        city: data.cityId,
        ward: data.wardId,
        cityNewId: data?.cityNewId,
        wardNewId: data?.wardNewId,
        regionName: initialRegion?.regionName || '',
        address: data.address,
        coordinatesX: data.latitude,
        coordinatesY: data.longitude,
        LineBusiness: data?.tcustomerLineProduct?.map(
          (item: any) => item.lineProductId,
        ),
        noteLineBusiness: data?.tcustomerLineProduct?.find(
          (item: any) => item?.lineProductId === 'K',
        )?.note,
      };

      const fileList = Object.keys(data)
        .filter((key) => key.startsWith('urlImage') && data[key] !== null)
        .map((key, index) => {
          const imgUrl = data[key];
          const fileName = imgUrl.substring(imgUrl.lastIndexOf('/') + 1);

          return {
            uid: `${index}`,
            url: imgUrl,
            name: fileName,
            status: 'done' as UploadFileStatus,
          };
        });

      if (fileList.length) setFileList(fileList);

      const isLineProductExisted = data?.tcustomerLineProduct?.some(
        (item: any) => item.lineProductId === 'K',
      );
      setIsOtherSelected(isLineProductExisted);

      setTypeCustomer(typeCustomer);
      setPrevValue({
        cccd: data?.cccd,
        taxCode: data?.taxId,
      });
      setCenter({
        lat: data.latitude,
        lng: data.longitude,
      });
      setSelectedAddress({
        city: data?.tcityNew?.name || data?.tcity?.name,
        district: data?.tdistrict?.name,
        ward: data?.twardNew?.name || data?.tward?.name,
        address: data?.address,
      });

      if (data?.cityId && data?.tcity?.name) {
        setOptionsCity([
          {
            label: data.tcity.name,
            value: data.cityId,
          },
        ]);
      } else {
        setOptionsCity([]);
      }
      if (data?.wardId && data?.tward?.name) {
        setOptionsWard([
          {
            label: data.tward.name,
            value: data.wardId,
          },
        ]);
      } else {
        setOptionsWard([]);
      }

      form.setFieldsValue(fieldsValue);
    } catch (error) {
      console.log(error);
    }
  }, [isEdit, form, data, regionItems]);

  const handleSaleLineCancel = () => {
    setIsOpenModal(false);
    form.resetFields();
    setFileList([]);
    setCenter(null);
    setIsOtherSelected(false);
    setTypeCustomer(initTypeCustomer);
    setOptionsWardNew([]);

    onCancel();
  };

  const checkFormValidate = async (form: FormInstance<any>) => {
    try {
      await form.validateFields();
      return true;
    } catch (error) {
      return false;
    }
  };

  const uploadImages = async (isSendToSAP?: boolean, amlData?: any) => {
    try {
      setIsUploading(true);
      const urlImageFields: any = {};

      for (const [index, file] of fileList.entries()) {
        if (file.originFileObj) {
          const formData = new FormData();
          formData.append('file', file.originFileObj);

          const response = await axiosGraphQL.post(
            '/file/uploadImage',
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            },
          );

          urlImageFields[`urlImage${index + 1}`] = response.data.filePath;
        } else if (file.url) urlImageFields[`urlImage${index + 1}`] = file.url;
      }

      for (let i = 1; i <= 5; i++) {
        const urlField = `urlImage${i}`;

        urlImageFields[urlField]
          ? form.setFieldValue(`${urlField}`, urlImageFields[urlField])
          : form.setFieldValue(`${urlField}`, null);
      }

      const { birthday, ...others } = form.getFieldsValue();
      const params = {
        isSendToSAP,
        birthday: birthday ? convertDateToBE(birthday) : null,
        ...others,
        ...urlImageFields,
        ...(amlData?.terrorismUserApproval && {
          terrorismType: amlData.terrorismType,
          terrorismUserApproval: amlData.terrorismUserApproval,
          terrorismUserDate: amlData.terrorismUserDate,
          terrorismUserPlant: amlData.terrorismUserPlant,
        }),
      };

      isEdit
        ? updateCustomer({ id: data.id, ...params })
        : createCusNew(params);
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const checkDuplicate = async (params: ICheckDuplicateParams) => {
    const { fieldName, fieldValue, errorMessage, apiFn } = params;
    const response = await apiFn(fieldValue);

    if (response?.data?.getCustomer?.items?.length > 0) {
      form.setFields([
        {
          name: fieldName,
          errors: errorMessage,
        },
      ]);
      return true;
    }

    return false;
  };

  const handleClearGender = () => {
    const genderError = form.getFieldError('Gender');
    genderError?.length && form.setFields([{ name: 'Gender', errors: [] }]);
  };

  const handleSaleLineSubmit = async (isSendToSAP?: boolean) => {
    handleClearGender();
    if (!(await checkFormValidate(form))) return;

    let { taxCode, cccd } = form.getFieldsValue();
    taxCode = taxCode?.trim();
    cccd = cccd?.trim();

    const isPersonCustomer = typeCustomer === 'Cá nhân';

    setIsCheckingDuplicate(true);
    try {
      const isDuplicateTaxId =
        taxCode && prevValue.taxCode?.trim() !== taxCode
          ? await checkDuplicate({
              fieldName: 'taxCode',
              fieldValue: taxCode,
              errorMessage: ['Mã số thuế đã tồn tại.'],
              apiFn: checkDuplicateTaxId,
            })
          : false;

      const isDuplicateCCCD =
        isPersonCustomer && cccd && prevValue.cccd?.trim() !== cccd
          ? await checkDuplicate({
              fieldName: 'cccd',
              fieldValue: cccd,
              errorMessage: ['CCCD đã tồn tại.'],
              apiFn: checkDuplicateCCCD,
            })
          : false;

      if (isDuplicateTaxId || isDuplicateCCCD) return;

      const { Surname, name, nameCustomer } = form.getFieldsValue();
      const customerName =
        typeCustomer === 'Cá nhân'
          ? `${Surname || ''} ${name || ''}`.trim()
          : nameCustomer || '';

      const tempCustomer = {
        id: data?.id,
        sapCustomerId: data?.sapCustomerId,
        name: typeCustomer === 'Cá nhân' ? customerName : name, // Tên người liên hệ cho Doanh nghiệp
        storeName: nameCustomer, // Tên doanh nghiệp
        firstName: typeCustomer === 'Cá nhân' ? Surname : '',
        Surname: Surname,
        Name: name,
      };

      checkAndProceed(tempCustomer, () => {
        uploadImages(isSendToSAP, tempCustomer);
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  const handleChangeAddress = (key: keyof ISelectedAddress, value?: string) => {
    let newAddress = {} as ISelectedAddress;
    const defaultAddress: ISelectedAddress = {
      city: undefined,
      ward: undefined,
      district: undefined,
    };

    if (key === 'city') newAddress = { ...defaultAddress, city: value };
    else
      newAddress =
        key === 'district'
          ? { ...selectedAddress, district: value, ward: undefined }
          : { ...selectedAddress, [key]: value };

    setSelectedAddress((prev) => ({
      ...prev,
      ...newAddress,
    }));
  };

  const handleChangeCity = (value: any, option?: DefaultOptionType) => {
    form.setFieldsValue({
      ward: undefined,
    });

    handleChangeAddress('city', option?.label?.toString());

    if (!value) {
      setOptionsWard([]);
      return;
    }

    mutationRegion.mutate({
      query: GET_REGION,
      variables: {
        input: {
          cityId: {
            equals: value,
          },
        },
      },
    });
  };

  const handleChangeDistrict = (value: any, option?: DefaultOptionType) => {
    form.setFieldsValue({
      ward: undefined,
    });

    handleChangeAddress('district', option?.label?.toString());

    if (!value || !option?.label) {
      setOptionsWard([]);
      return;
    }

    mutationWard.mutate({
      query: GET_ALL_WARD,
      variables: {
        input: {
          districtId: {
            equals: value,
          },
        },
      },
    });

    setSelectedAddress((prev) => ({
      ...prev,
      district: option.label?.toString(),
    }));
  };

  const handleCloseModal = () => {
    setIsOpenModal(false);

    form.resetFields();
    setFileList([]);
    setTypeCustomer(undefined);
    setIsOtherSelected(false);
    setIsSAPCustomer(false);
    setCenter(null);
    setOptionsWardNew([]);
  };

  const updateLocation = (point: any) => {
    if (point === null || point === undefined) {
      toastError('Chưa chọn vị trí để cập nhật');
      return;
    }

    form.setFieldsValue({
      coordinatesX: point?.lat?.toString(),
      coordinatesY: point?.lng?.toString(),
    });

    setCenter(point);
  };

  const handleClearError = async () => {
    setSelectedIdType('cccd');
    const fields = ['taxCode', 'sdt', 'Gender', 'cccd', 'passportId'];
    const newFieldsValue = fields.map((name) => ({
      name,
      errors: [],
    }));

    form.setFields(newFieldsValue);
    await new Promise((resolve) => setTimeout(resolve, 0));

    const { Surname, name } = form.getFieldsValue();
    const newFields = [
      ...fields,
      ...(Surname ? ['Surname'] : []),
      ...(name ? ['name'] : []),
    ];

    form.validateFields(newFields);
  };

  const handleChangeTypeCus = async (value: any) => {
    setTypeCustomer(value);
    await handleClearError();

    form.setFieldsValue({
      DanhXung: value == 'Doanh nghiệp' ? 'Công ty' : undefined,
    });

    const customerType = form.getFieldValue('typeCustomer');
    getKHCha({
      currentCust: data?.id,
      mainCustomer: data?.mainCustomer,
      typeCust: customerType === 'Doanh nghiệp' ? '2' : '1',
    });
  };

  const onChangeFile = (info: any) => {
    info.file.status = 'done';
    let newFileList = [...info.fileList];

    if (newFileList.length > 10) {
      toastError('Chỉ được phép chọn tối đa 10 hình ảnh.');
      newFileList = newFileList.slice(0, 10);
    }

    setFileList(newFileList);
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      if (file.originFileObj) {
        file.preview = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(file.originFileObj as any);
          reader.onload = () => resolve(reader.result as string);
        });
      }
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
    setPreviewTitle(
      file.name ||
        (file.url ? file.url.substring(file.url.lastIndexOf('/') + 1) : ''),
    );
  };

  const handleSelectChange = (value: any) => {
    setIsOtherSelected(value.includes('K'));
  };

  const handleScrollChange = (params: ILazySelectParams) => {
    const customerType = form.getFieldValue('typeCustomer');

    const customerParams = {
      ...params,
      currentCust: data?.id,
      mainCustomer: data?.mainCustomer,
      typeCust: customerType === 'Doanh nghiệp' ? '2' : '1',
    };
    getKHCha(customerParams);
  };

  const handleValidateFormSendToSAP = async () => {
    const {
      taxCode,
      sdt,
      email,
      cccd,
      passportId,
      Gender,
      cityNewId,
      wardNewId,
    } = form.getFieldsValue();
    const isPersonCustomer = typeCustomer === 'Cá nhân';

    const validateFields = (fields: any[]) =>
      fields
        .filter(([condition]: any) => !condition)
        .map(([_, name, message]: any) => ({
          name,
          errors: [message],
        }));

    form.setFields(
      validateFields([
        [sdt, 'sdt', 'Vui lòng nhập số điện thoại'],
        [cityNewId, 'cityNewId', 'Vui lòng chọn tỉnh/thành phố mới'],
        [wardNewId, 'wardNewId', 'Vui lòng chọn phường/xã mới'],
        ...(isPersonCustomer
          ? [
              selectedIdType === 'cccd'
                ? [cccd, 'cccd', 'Vui lòng nhập CCCD']
                : [passportId, 'passportId', 'Vui lòng nhập Passport'],
              [Gender, 'Gender', 'Vui lòng chọn giới tính'],
            ]
          : [
              [taxCode, 'taxCode', 'Vui lòng nhập mã số thuế'],
              [email, 'email', 'Vui lòng nhập email'],
            ]),
      ]),
    );
  };

  const handleClickSendCustomerToSAP = async () => {
    const isValid = await checkFormValidate(form);
    await handleValidateFormSendToSAP();

    if (!isValid) return;

    const errorFields = form.getFieldsError();
    const isHasErrorField =
      errorFields?.filter((field) => field.errors.length > 0).length > 0;

    if (isHasErrorField) return;

    const mainCustomerId = form.getFieldValue('IDCusParent');
    const isHasMainCustomer =
      mainCustomerId ||
      !!(await customerMutation.mutateAsync(data?.id)).data?.getCustomer
        ?.items?.[0]?.mainCustomerId;

    isHasMainCustomer && handleSaleLineSubmit(true);
    setIsOpenNotifyModal(!isHasMainCustomer);
  };

  const handleInputAddressChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const value = rawValue
      ? rawValue.charAt(0).toUpperCase() + rawValue.slice(1)
      : rawValue;
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

    debounceTimeout.current = setTimeout(
      () => handleChangeAddress('address', value),
      1000,
    );
  };

  const handleTaxCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value,
      valueWithoutHypen = value.replace(/\D/g, '');

    let formattedValue = valueWithoutHypen;
    if (valueWithoutHypen.length === 13)
      formattedValue = `${valueWithoutHypen.slice(0, 10)}-${valueWithoutHypen.slice(10)}`;

    form.setFieldValue('taxCode', formattedValue);
  };

  const shouldShowCCCD = useMemo(() => {
    if (isEdit) {
      if (data?.cccd && data?.passportId) {
        return true;
      }
      return selectedIdType === 'cccd';
    }
    return selectedIdType === 'cccd';
  }, [isEdit, data?.cccd, data?.passportId, selectedIdType]);

  const shouldShowPassport = useMemo(() => {
    if (isEdit) {
      if (data?.cccd && data?.passportId) {
        return true;
      }
      return selectedIdType === 'passport';
    }
    return selectedIdType === 'passport';
  }, [isEdit, data?.cccd, data?.passportId, selectedIdType]);

  useEffect(() => {
    if (isEdit && data) {
      if (data.cccd && data.cccd != '') {
        setSelectedIdType('cccd');
      } else if (data.passportId && data.passportId != '') {
        setSelectedIdType('passport');
      }
    }
  }, [isEdit, data]);

  const handleIdTypeChange = (value: 'cccd' | 'passport') => {
    setSelectedIdType(value);
    if (!isEdit || !(data?.cccd && data?.passportId)) {
      if (value === 'cccd') {
        form.setFieldValue('passportId', undefined);
      } else {
        form.setFieldValue('cccd', undefined);
      }
    }
  };

  const isSubmitting = useMemo(
    () =>
      isCheckingDuplicate ||
      isUploading ||
      isCreateCusPending ||
      isUpdateCustomerPending ||
      isCreatePlantPending ||
      isCreateLinePending,
    [
      isCheckingDuplicate,
      isUploading,
      isCreateCusPending,
      isUpdateCustomerPending,
      isCreatePlantPending,
      isCreateLinePending,
    ],
  );

  return (
    <>
      <Modal
        open={isOpenModal}
        title={isEdit ? 'Cập nhật thông tin khách hàng' : 'Tạo khách hàng mới'}
        classNames={{
          content: '!p-0',
          header:
            '!px-[24px] !pt-[20px] !pb-[8px] !border-sub !border-solid !border-b-[1px]',
          body: '!px-[42px]',
          footer: '!px-[24px] !py-[20px] !bg-rowTable !rounded-b-[8px]',
        }}
        className="!w-max  xl:!w-[1280px] lg:!w-[1023px] md:!w-[767px] sm:!w-[639px] !rounded-[8px]  "
        onCancel={handleSaleLineCancel}
        footer={
          <Flex align="center" justify="space-between">
            {isEdit && (
              <Button
                title="Lấy mã SAP"
                displayType="approve"
                iconBefore={<Loader />}
                disabled={!!data?.sapCustomerId || isSendCustomerPending}
                handleOnclick={handleClickSendCustomerToSAP}
              />
            )}
            <Flex align="center" justify="end" flex={1}>
              <Button
                fill
                title="Hủy"
                disabled={isSendCustomerPending}
                customClass="!bg-rowTable !border-transparent !text-neutral-30"
                handleOnclick={handleSaleLineCancel}
              />
              <Button
                fill
                title={isEdit ? 'Lưu' : 'Tạo'}
                iconBefore={<PlusOutlined />}
                loading={isSubmitting}
                disabled={isSubmitting || isSendCustomerPending}
                handleOnclick={() => handleSaleLineSubmit()}
              />
            </Flex>
          </Flex>
        }
      >
        <Form
          form={form}
          autoComplete="off"
          layout="vertical"
          initialValues={{
            gender: 'male',
            status: 'active',
          }}
        >
          <Row gutter={[8, 0]} align={'middle'}>
            <Col span={24}>
              <label className="font-bold">1. Thông tin khách hàng</label>
            </Col>
            <Col span={8}>
              <Form.Item
                name="typeCustomer"
                label="Loại khách hàng"
                initialValue={'Doanh nghiệp'}
                rules={
                  isSAPCustomer
                    ? undefined
                    : [
                        {
                          required: true,
                          message: 'Vui lòng chọn loại khách hàng',
                        },
                      ]
                }
              >
                <SelectForm
                  allowClear={false}
                  disabled={isSAPCustomer}
                  options={typeCustomerOptions}
                  onChange={handleChangeTypeCus}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="DanhXung"
                label="Danh xưng"
                initialValue={'Công ty'}
                rules={
                  isSAPCustomer
                    ? undefined
                    : [
                        {
                          required: typeCustomer === 'Cá nhân',
                          message: 'Vui lòng chọn danh xưng',
                        },
                      ]
                }
              >
                {typeCustomer === 'Cá nhân' ? (
                  <SelectForm
                    disabled={isSAPCustomer}
                    options={GenderOptions.slice(0, 2)}
                  />
                ) : (
                  <InputForm
                    readOnly
                    value={'Công ty'}
                    // defaultValue={'Công ty'}
                    placeholder="Nhập danh xưng"
                    disabled={typeCustomer === 'Doanh nghiệp' || isSAPCustomer}
                  />
                )}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Khách hàng cha" name="IDCusParent">
                <LazySelectForm
                  data={mainCustomerData}
                  disabled={isMainCustomer || isSAPCustomer}
                  placeholder="Chọn mã Khách hàng cha"
                  filterSort={(optionA: any, optionB: any) => {
                    if (optionA.key === data?.mainCustomer?.id) return -1;
                    if (optionB.key === data?.mainCustomer?.id) return 1;
                    return (optionA.label ?? '')
                      .toLowerCase()
                      .localeCompare((optionB.label ?? '').toLowerCase());
                  }}
                  onSelectScrollChange={handleScrollChange}
                />
              </Form.Item>
            </Col>
            {typeCustomer === 'Cá nhân' ? (
              <>
                <Col span={8}>
                  <Form.Item
                    label="Họ"
                    name="Surname"
                    rules={
                      isSAPCustomer
                        ? undefined
                        : [
                            {
                              required: true,
                              message: 'Vui lòng nhập họ khách hàng',
                            },
                            typeCustomer === 'Cá nhân'
                              ? {
                                  max: 40,
                                  message: 'Họ chỉ được tối đa 40 ký tự',
                                }
                              : {},
                          ]
                    }
                  >
                    <InputForm
                      disabled={isSAPCustomer}
                      placeholder="Nhập họ khách hàng"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Tên"
                    name="name"
                    rules={
                      isSAPCustomer
                        ? undefined
                        : [
                            {
                              required: true,
                              message: 'Vui lòng nhập tên khách hàng',
                            },
                            typeCustomer === 'Cá nhân'
                              ? {
                                  max: 40,
                                  message: 'Tên chỉ được tối đa 40 ký tự',
                                }
                              : {},
                          ]
                    }
                  >
                    <InputForm
                      disabled={isSAPCustomer}
                      placeholder="Nhập tên khách hàng"
                    />
                  </Form.Item>
                </Col>
              </>
            ) : (
              <Col span={16}>
                <Form.Item
                  label="Tên khách hàng"
                  name="nameCustomer"
                  rules={
                    isSAPCustomer
                      ? undefined
                      : [
                          {
                            required: true,
                            message: 'Vui lòng nhập tên khách hàng',
                          },
                        ]
                  }
                >
                  <InputForm
                    disabled={isSAPCustomer}
                    placeholder="Nhập tên khách hàng"
                  />
                </Form.Item>
              </Col>
            )}

            <Col span={8}>
              <Form.Item
                label="Tên ngắn"
                name="nameShort"
                rules={
                  isSAPCustomer
                    ? undefined
                    : [
                        {
                          required: true,
                          message: 'Vui lòng nhập tên ngắn',
                        },
                      ]
                }
              >
                <InputForm
                  disabled={isSAPCustomer}
                  placeholder="Nhập tên ngắn"
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <label className="font-bold">2. Thông tin liên hệ</label>
            </Col>
            {typeCustomer !== 'Cá nhân' && (
              <Col span={8}>
                <Form.Item
                  rules={
                    isSAPCustomer
                      ? undefined
                      : [
                          {
                            required: true,
                            message: 'Vui lòng nhập tên người liên hệ',
                          },
                        ]
                  }
                  label="Tên người liên hệ"
                  name="name"
                >
                  <InputForm
                    disabled={isSAPCustomer}
                    placeholder="Nhập tên người liên hệ"
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={8}>
              <Form.Item
                label="Mã số thuế"
                name="taxCode"
                rules={
                  isSAPCustomer
                    ? undefined
                    : [
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();

                            const valueWithoutHypen = value.replace(/\D/g, '');
                            const valueLength = valueWithoutHypen.length;

                            const isPerson =
                              form.getFieldValue('typeCustomer') === 'Cá nhân';
                            if (isPerson) {
                              if (
                                valueLength !== 10 &&
                                valueLength !== 12 &&
                                valueLength !== 13
                              ) {
                                return Promise.reject(
                                  'Mã số thuế khách hàng cá nhân phải có 10, 12 hoặc 13 chữ số',
                                );
                              }
                            } else {
                              if (valueLength !== 10 && valueLength !== 13) {
                                return Promise.reject(
                                  'Mã số thuế doanh nghiệp phải có 10 hoặc 13 chữ số',
                                );
                              }
                            }

                            return Promise.resolve();
                          },
                        },
                      ]
                }
              >
                <InputForm
                  disabled={isSAPCustomer}
                  placeholder="Nhập mã số thuế"
                  onChange={handleTaxCodeChange}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="sdt"
                label="Số điện thoại"
                rules={
                  !isSAPCustomer
                    ? [
                        {
                          pattern: /^[0-9]{10}$/,
                          message: 'Số điện thoại không hợp lệ',
                        },
                      ]
                    : []
                }
              >
                <InputForm
                  disabled={isSAPCustomer}
                  placeholder="Nhập số điện thoại"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="Gender" label="Giới tính">
                <SelectForm
                  placeholder="Chọn giới tính"
                  disabled={isSAPCustomer}
                  options={GenderOptions}
                  onSelect={handleClearGender}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Sinh nhật"
                name="birthday"
                rules={[
                  {
                    required: isSAPCustomer ? false : true,
                    message: 'Vui lòng chọn ngày sinh',
                  },
                ]}
              >
                <DatePicker
                  size="large"
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  disabled={isSAPCustomer}
                  placeholder="Chọn sinh nhật"
                  disabledDate={(current) =>
                    current && current > dayjs().endOf('day')
                  }
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Email"
                name="email"
                rules={
                  isSAPCustomer
                    ? undefined
                    : [
                        {
                          type: 'email',
                          message: 'Email không hợp lệ',
                        },
                      ]
                }
              >
                <InputForm disabled={isSAPCustomer} placeholder="Nhập email" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="Loại giấy tờ">
                <SelectForm
                  value={selectedIdType}
                  disabled={isSAPCustomer}
                  defaultValue={'cccd'}
                  options={
                    typeCustomer === 'Doanh nghiệp'
                      ? idTypeOptionsDN
                      : idTypeOptionsCN
                  }
                  allowClear={false}
                  placeholder="Chọn loại giấy tờ"
                  onChange={handleIdTypeChange}
                />
              </Form.Item>
            </Col>

            {shouldShowCCCD && (
              <Col span={8}>
                <Form.Item
                  label="CCCD"
                  name="cccd"
                  rules={[
                    {
                      required: selectedIdType === 'cccd' && !isSAPCustomer,
                      message: 'Vui lòng nhập số CCCD',
                    },
                    {
                      pattern: /^[0-9]{12}$/,
                      message: 'CCCD phải có 12 chữ số',
                    },
                  ]}
                >
                  <InputForm
                    disabled={isSAPCustomer}
                    placeholder="Nhập số CCCD"
                  />
                </Form.Item>
              </Col>
            )}

            {shouldShowPassport && (
              <Col span={8}>
                <Form.Item
                  label="Passport"
                  name="passportId"
                  rules={[
                    {
                      required: selectedIdType === 'passport' && !isSAPCustomer,
                      message: 'Vui lòng nhập Passport',
                    },
                    {
                      pattern: /^[A-Za-z][0-9]{7}$/,
                      message:
                        'Passport phải bắt đầu bằng chữ cái và có 7 chữ số',
                    },
                  ]}
                >
                  <InputForm
                    disabled={isSAPCustomer}
                    placeholder="Nhập số Passport"
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <label className="font-bold">3. Địa chỉ liên hệ</label>
            </Col>

            {isEdit && (
              <>
                <Col span={12}>
                  <Form.Item
                    name="city"
                    label="Tỉnh/thành"
                    rules={[
                      {
                        required: false,
                        message: 'Vui lòng chọn tỉnh/thành',
                      },
                    ]}
                  >
                    <SelectForm
                      disabled={isEdit}
                      options={optionsCity}
                      placeholder="Chọn tỉnh/thành"
                      onChange={handleChangeCity}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Phường/xã"
                    name="ward"
                    rules={[
                      {
                        // required: true,
                        message: 'Vui lòng chọn phường/xã',
                      },
                    ]}
                  >
                    <SelectForm
                      disabled={isEdit}
                      options={optionsWard}
                      placeholder="Chọn phường/xã"
                      onChange={(_, option?: DefaultOptionType) =>
                        handleChangeAddress('ward', option?.label?.toString())
                      }
                    />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col span={12}>
              <Form.Item
                name="cityNewId"
                label="Tỉnh/Thành phố mới"
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng chọn tỉnh/thành phố mới',
                  },
                ]}
              >
                <SelectForm
                  options={optionsCityNew}
                  placeholder="Chọn tỉnh/thành phố mới"
                  onChange={handleChangeCityNew}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Phường/Xã mới"
                name="wardNewId"
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng chọn phường/xã mới',
                  },
                ]}
              >
                <SelectForm
                  options={optionsWardNew}
                  placeholder="Chọn phường/xã mới"
                  onChange={handleChangeWardNew}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Tên Region" name="regionName">
                <InputForm
                  disabled
                  placeholder="Tên Region (tự động hiển thị theo Tỉnh và Phường mới)"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Địa chỉ"
                name="address"
                normalize={(value) =>
                  value ? value.charAt(0).toUpperCase() + value.slice(1) : value
                }
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng nhập địa chỉ',
                  },
                ]}
              >
                <InputForm
                  placeholder="Nhập địa chỉ"
                  onChange={(e) => handleInputAddressChange(e)}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Button
                fill
                title="Lấy tọa độ"
                customClass="!w-full"
                iconBefore={<EnvironmentOutlined />}
                handleOnclick={() => setIsOpenToaDo(true)}
              />
            </Col>
            <Col span={8}>
              <Form.Item
                label="Tọa độ X"
                name="coordinatesX"
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng lấy toạ độ',
                  },
                ]}
              >
                <InputForm disabled placeholder="Nhập tọa độ" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Tọa độ Y"
                name="coordinatesY"
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng lấy toạ độ',
                  },
                ]}
              >
                <InputForm disabled placeholder="Nhập tọa độ" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <label className="font-bold">4. Hình ảnh</label>
            </Col>
            <Col style={{ paddingLeft: 0, paddingRight: 0 }} span={24}>
              <Upload
                fileList={fileList}
                accept=".png,.jpg,.jpeg"
                listType="picture-card"
                onChange={onChangeFile}
                beforeUpload={(file) => {
                  const isImage =
                    file.type === 'image/png' ||
                    file.type === 'image/jpeg' ||
                    file.type === 'image/jpg';

                  if (!isImage) {
                    toastError(
                      'Chỉ được phép chọn file hình ảnh với định dạng PNG, JPG, hoặc JPEG',
                    );
                  }
                  return isImage || Upload.LIST_IGNORE;
                }}
                multiple={true}
                onPreview={handlePreview}
                showUploadList={{
                  showPreviewIcon: true,
                  showRemoveIcon: true,
                  showDownloadIcon: false,
                }}
                className="flex flex-row-reverse"
              >
                <ImportIcon className="w-9 h-auto text-primary-40 " />
              </Upload>
            </Col>
            <Col span={24}>
              <label className="font-bold">5. Ghi chú</label>
            </Col>
            <Col span={8}>
              <Form.Item label="Dòng hàng kinh doanh" name="LineBusiness">
                <SelectForm_v2
                  mode="multiple"
                  allowClear={false}
                  options={noteOptions}
                  onChange={handleSelectChange}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      {isOtherSelected && (
                        <Form.Item
                          name="noteLineBusiness"
                          className="px-[5px] mt-3"
                        >
                          <Input.TextArea
                            rows={3}
                            // value={note}
                            className="!w-full"
                            placeholder="Nhập thông tin"
                            // onChange={(e) => setNote(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        </Form.Item>
                      )}
                    </>
                  )}
                ></SelectForm_v2>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Ghi chú" name="note">
                <Input.TextArea rows={4} placeholder="Nhập ghi chú" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <ModalMapLocation
        open={isOpenToaDo}
        placement={placement}
        title={'Cập nhật vị trí'}
        center={{
          // ...center,
          lat: parseFloat(center?.lat) || null,
          lng: parseFloat(center?.lng) || null,
        }}
        setOpen={setIsOpenToaDo}
        updateLocation={updateLocation}
      />
      <Modal
        zIndex={2000}
        title="Thông báo"
        open={isOpenNotifyModal}
        footer={
          <Flex gap={8} justify="flex-end">
            <Button
              title="Quay lại"
              handleOnclick={() => setIsOpenNotifyModal(false)}
            />
            <Button
              fill
              title="Tạo mã SAP"
              handleOnclick={() => {
                handleSaleLineSubmit(true);
                setIsOpenNotifyModal(false);
              }}
            />
          </Flex>
        }
        onCancel={() => setIsOpenNotifyModal(false)}
      >
        <p className="text-base">
          Khách hàng này có mối quan hệ cha - con nào không?
        </p>
      </Modal>

      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
      {amlModalContext}
    </>
  );
};

export default memo(ModalAddCusPage);
